/**
 * When a JWT mint that hit its timeout is worth a Sentry event — the rule both
 * clients' caches (`flashka-web/src/auth/better/client.ts`,
 * `flashka-app/src/auth/better/jwt.ts`) report by.
 *
 * A hang only points at the auth worker when the client could have been
 * answered: in the foreground for the whole wait, online, and with a timer that
 * fired roughly when it was due. A laptop waking from sleep with the tab in the
 * background is none of those — FLASHKA-VITE 7800902876 was exactly that (hidden
 * tab, 17 h of silence, a failed fetch on wake, then the mint), the case the
 * `page_visible` / `online` tags added for FLASHKA-VITE-2XX were there to spot.
 */
export interface TokenTimeoutWindow {
  /**
   * Web: the document was visible. Mobile: `AppState` was neither `background`
   * nor `inactive` (`unknown` before the first AppState event counts as front).
   */
  foregroundAtStart: boolean;
  foregroundAtTimeout: boolean;
  online: boolean;
  /** Wall-clock ms from the request starting to the timer firing. */
  elapsedMs: number;
  timeoutMs: number;
}

/**
 * A timer that fires this much later than it was due was frozen — a machine
 * asleep, or a hidden tab's throttled timers — so the wait it measured is the
 * client's, not the server's.
 */
const LATE_TIMER_FACTOR = 1.5;

/** True only when the client could have been answered, so the hang is the server's. */
export function isAnswerableTokenTimeout(w: TokenTimeoutWindow): boolean {
  return (
    w.foregroundAtStart &&
    w.foregroundAtTimeout &&
    w.online &&
    w.elapsedMs < w.timeoutMs * LATE_TIMER_FACTOR
  );
}

/**
 * How long a mint may stay unanswered before it is a HANG worth an event. The
 * 10 s timeout only stops callers waiting — a token that lands after it is kept
 * — so a report at the timeout counted slow-but-answered mints as failures:
 * 1,979 users a week on FLASHKA-VITE-2XX, every one visible and online, the
 * one inspected answered at ~12 s. Past this, nothing is coming back.
 */
export const TOKEN_HANG_MS = 30_000;

/**
 * After a mint has timed out: `onHang` if it is still unanswered `hangMs` after
 * it started, else `onSettled` with how long it took — never both.
 */
export function watchTokenHang(
  request: Promise<unknown>,
  opts: {
    startedAtMs: number;
    onHang: (elapsedMs: number) => void;
    onSettled?: (elapsedMs: number) => void;
    hangMs?: number;
  },
): void {
  const hangMs = opts.hangMs ?? TOKEN_HANG_MS;
  let done = false;
  const handle = setTimeout(
    () => {
      if (done) return;
      done = true;
      opts.onHang(Date.now() - opts.startedAtMs);
    },
    Math.max(0, hangMs - (Date.now() - opts.startedAtMs)),
  );
  const settle = () => {
    if (done) return;
    done = true;
    clearTimeout(handle);
    opts.onSettled?.(Date.now() - opts.startedAtMs);
  };
  request.then(settle, settle);
}
